import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CUSTOM_CALLABLE_WIDGET_TYPE,
  MECHANISM_WIDGET_TYPE,
  MOTOR_MULTI_WIDGET_TYPE,
  MOTOR_WIDGET_TYPE,
  OTHER_WIDGET_TYPE,
  SENSOR_WIDGET_TYPE,
  motorPortDisplayTitle,
  mergeElectronicsPortStreamGraphs,
  motorMultiWidgetDisplayTitle,
  parseMotorMultiWidgetDataKey,
  drivebaseWidgetStreamDataKey,
  streamMetricSlug,
  type ElectronicsPortStreamKind,
} from '../../Graphs/sensor-utils';
import type { Graph, WidgetOptions } from '../dashboardTypes';
import { CurveType } from '@mui/x-charts';
import { Box, MenuItem } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import LineGraphWidget from './LineGraphWidget';
import LiveNumberWidget from './LiveNumberWidget';
import RawDataWidget from './RawDataWidget';
import BarGraphWidget from './BarGraphWidget';
import { useLiveGraphKeys } from '../LiveGraphStore';
import { useAppSelector } from '../../../redux/ReduxHooks';
import { find, propEq } from 'ramda';
import { useWidgetHeader } from './WidgetHeaderContext';
import {
  defaultWidgetTitle,
  isCustomCallableStreamRegisteredInCode,
  resolveCustomCallableDisplayName,
  resolveMotorMechanismWidgetTitle,
  resolveOtherWidgetTitle,
  resolveSensorWidgetTitle,
} from './widgetTitle';
import { GraphNoDataMessage } from '../../Graphs/GraphNoDataMessage';
import ChooseMotorDataToolbarButton from './ChooseMotorDataToolbarButton';
import {
  buildMechanismClassNameByPort,
  buildMechanismDrawerPortLabelByPort,
  buildMechanismVariableNameByPort,
  buildMotorClassNameByPort,
  buildMotorChartSeriesLabelByPort,
  buildOtherElectronicsChartSeriesLabelByPort,
  buildSensorChartSeriesLabelByPort,
  buildSensorVariableNameByPort,
  clampMotorMultiMetrics,
  defaultSelectedMotorMetrics,
  defaultSelectedMotorPorts,
  defaultSelectedOtherPorts,
  defaultSelectedSensorPorts,
  getMultiAxisDataPickerEntries,
  getMechanismPickerEntries,
  getMotorPickerEntries,
  getOtherPickerEntries,
  getSensorPickerEntries,
  motorMultiStreamKeysForMetrics,
  motorStreamKeysForPorts,
  motorStreamKeysForSelectedPorts,
  otherStreamKeysForPorts,
  sensorClassNameFromStreamKey,
  sensorStreamKeysForPorts,
} from '../../../utils/electronicsDashboardUtils';
import { otherClassNameFromStreamKey } from '../../Graphs/sensor-utils';
import type { ChartSeriesLabelOptions } from '../../Graphs/chartSeriesLabel';
import { portDisplayLabelMapFromWidgetOptions } from '../dashboardHistoricUtils';
import { useRegisteredCustomCallableParameters } from '../CustomCallableStreamsContext';
import { dedupeStreamKeysPreserveOrder, resolveWidgetSensorKeys } from '../../../utils/sensor-stream-tracker';
import {
  filterWidgetPorts,
  orderedMetricsKey,
  resolveHistoricOrLiveLabelMap,
  shouldShowAllMotorPortPicker,
  shouldShowAllOtherPortPicker,
  shouldShowAllSensorPortPicker,
  sortedPortsKey,
} from './dashboardWidgetPortUtils';
import { useChooseOtherElectronicsToolbar } from './useChooseOtherElectronicsToolbar';
import { useChooseMechanismsToolbar } from './useChooseMechanismsToolbar';
import { useChooseMotorsToolbar } from './useChooseMotorsToolbar';
import { useChooseSensorsToolbar } from './useChooseSensorsToolbar';
import { useMotorStreamPortsChange } from './useMotorStreamPortsChange';
import { useDashboardCapabilities } from '../dashboardCapabilities';
import { usePersistDashboardWidgetContent } from './usePersistDashboardWidgetContent';

const GRAPH_WIDGET_STYLES = ['line', 'number', 'raw', 'bar'] as const;
type GraphWidgetStyle = (typeof GRAPH_WIDGET_STYLES)[number];

function isGraphWidgetStyle(style: string): style is GraphWidgetStyle {
  return (GRAPH_WIDGET_STYLES as readonly string[]).includes(style);
}

interface GraphWidgetProps {
  type: string;
  id: string;
  dataKey: string;
  curve: CurveType;
  style: string;
  /** Resolved stream keys from widget layout content (used for historic multi-axis lookup). */
  sensorKeys?: string[];
  options?: WidgetOptions;
}

const GraphWidget = ({ type, id, dataKey, curve, style, sensorKeys: layoutSensorKeys, options }: GraphWidgetProps) => {
  const [graphCurve, setGraphCurve] = useState<CurveType>('linear');
  const [widgetStyle, setWidgetStyle] = useState<string>('line');

  const driveUnitPorts = useAppSelector((state) => state.application.driveUnitPorts);
  const projectCode = useAppSelector((state) => state.project.loadedProject?.program?.code ?? '');
  const { persistWidgetContent } = usePersistDashboardWidgetContent(id);
  const { isHistoric, canEditWidgetDataSelection } = useDashboardCapabilities();
  const { t } = useTranslation();

  const isMotorWidget = type === MOTOR_WIDGET_TYPE;
  const isMotorMultiWidget = type === MOTOR_MULTI_WIDGET_TYPE;
  const isMechanismWidget = type === MECHANISM_WIDGET_TYPE;
  const isSensorWidget = type === SENSOR_WIDGET_TYPE;
  const isOtherWidget = type === OTHER_WIDGET_TYPE;
  const isCustomCallableWidget = type === CUSTOM_CALLABLE_WIDGET_TYPE;
  const effectiveStyle: GraphWidgetStyle = isOtherWidget ? 'number' : isGraphWidgetStyle(widgetStyle) ? widgetStyle : 'line';

  const electronicsPortStreamKind = useMemo((): ElectronicsPortStreamKind | null => {
    if (isMotorWidget || isMotorMultiWidget || isMechanismWidget) return MOTOR_WIDGET_TYPE;
    if (isSensorWidget) return SENSOR_WIDGET_TYPE;
    if (isOtherWidget) return OTHER_WIDGET_TYPE;
    return null;
  }, [isMechanismWidget, isMotorMultiWidget, isMotorWidget, isOtherWidget, isSensorWidget]);

  const motorMultiPort = useMemo(() => {
    if (!isMotorMultiWidget) return null;
    if (options?.selectedMotorPort !== undefined) return options.selectedMotorPort;
    return parseMotorMultiWidgetDataKey(dataKey)?.port ?? null;
  }, [dataKey, isMotorMultiWidget, options?.selectedMotorPort]);
  const sensorClassName = useMemo(
    () => (isSensorWidget ? options?.sensorClassName ?? sensorClassNameFromStreamKey(dataKey) : null),
    [dataKey, isSensorWidget, options?.sensorClassName]
  );
  const otherClassName = useMemo(
    () => (isOtherWidget ? options?.otherClassName ?? otherClassNameFromStreamKey(dataKey) : null),
    [dataKey, isOtherWidget, options?.otherClassName]
  );

  const historicPortDisplayLabels = useMemo(() => portDisplayLabelMapFromWidgetOptions(options), [options]);

  const motorPortLabelByPort = useMemo(
    () =>
      resolveHistoricOrLiveLabelMap(isHistoric, historicPortDisplayLabels, () =>
        isMotorWidget ? buildMotorChartSeriesLabelByPort(driveUnitPorts, projectCode) : new Map<number, string>()
      ),
    [driveUnitPorts, historicPortDisplayLabels, isHistoric, isMotorWidget, projectCode]
  );
  const mechanismPortLabelByPort = useMemo(
    () =>
      resolveHistoricOrLiveLabelMap(isHistoric, historicPortDisplayLabels, () =>
        isMechanismWidget ? buildMechanismDrawerPortLabelByPort(driveUnitPorts, projectCode) : new Map<number, string>()
      ),
    [driveUnitPorts, historicPortDisplayLabels, isHistoric, isMechanismWidget, projectCode]
  );
  const sensorPortLabelByPort = useMemo(
    () =>
      resolveHistoricOrLiveLabelMap(isHistoric, historicPortDisplayLabels, () =>
        isSensorWidget && sensorClassName
          ? buildSensorChartSeriesLabelByPort(driveUnitPorts, projectCode, sensorClassName)
          : new Map<number, string>()
      ),
    [driveUnitPorts, historicPortDisplayLabels, isHistoric, isSensorWidget, projectCode, sensorClassName]
  );
  const otherPortLabelByPort = useMemo(
    () =>
      resolveHistoricOrLiveLabelMap(isHistoric, historicPortDisplayLabels, () =>
        isOtherWidget && otherClassName
          ? buildOtherElectronicsChartSeriesLabelByPort(driveUnitPorts, projectCode, otherClassName)
          : new Map<number, string>()
      ),
    [driveUnitPorts, historicPortDisplayLabels, isHistoric, isOtherWidget, otherClassName, projectCode]
  );

  const motorPickerEntries = useMemo(
    () => (isMotorWidget ? getMotorPickerEntries(driveUnitPorts, projectCode) : []),
    [driveUnitPorts, isMotorWidget, projectCode]
  );
  const mechanismPickerEntries = useMemo(
    () => (isMechanismWidget ? getMechanismPickerEntries(driveUnitPorts, projectCode) : []),
    [driveUnitPorts, isMechanismWidget, projectCode]
  );
  const motorDataPickerEntries = useMemo(() => {
    if (!isMotorMultiWidget || motorMultiPort === null) return [];
    const streamPrefix = parseMotorMultiWidgetDataKey(dataKey)?.prefix ?? 'motor';
    return getMultiAxisDataPickerEntries(motorMultiPort, streamPrefix, driveUnitPorts, projectCode);
  }, [dataKey, driveUnitPorts, isMotorMultiWidget, motorMultiPort, projectCode]);
  const availableMotorMetrics = useMemo(() => motorDataPickerEntries.map((entry) => entry.metric), [motorDataPickerEntries]);
  const sensorPickerEntries = useMemo(
    () => (isSensorWidget && sensorClassName ? getSensorPickerEntries(driveUnitPorts, projectCode, sensorClassName) : []),
    [driveUnitPorts, isSensorWidget, projectCode, sensorClassName]
  );
  const otherPickerEntries = useMemo(
    () => (isOtherWidget && otherClassName ? getOtherPickerEntries(driveUnitPorts, projectCode, otherClassName) : []),
    [driveUnitPorts, isOtherWidget, otherClassName, projectCode]
  );

  const availableMotorPorts = useMemo(() => motorPickerEntries.map((entry) => entry.port), [motorPickerEntries]);
  const availableMechanismPorts = useMemo(() => mechanismPickerEntries.map((entry) => entry.port), [mechanismPickerEntries]);
  const availableSensorPorts = useMemo(() => sensorPickerEntries.map((entry) => entry.port), [sensorPickerEntries]);
  const availableOtherPorts = useMemo(() => otherPickerEntries.map((entry) => entry.port), [otherPickerEntries]);

  const selectedMotorPortsKey = sortedPortsKey(options?.selectedMotorPorts);
  const selectedMechanismPortsKey = sortedPortsKey(options?.selectedMotorPorts);
  const selectedSensorPortsKey = sortedPortsKey(options?.selectedSensorPorts);
  const selectedOtherPortsKey = sortedPortsKey(options?.selectedOtherPorts);
  const selectedMotorMetricsKey = orderedMetricsKey(options?.selectedMotorMetrics);

  const selectedMotorPorts = useMemo(() => {
    if (!isMotorWidget) return [];
    const fromOptions = options?.selectedMotorPorts;
    if (fromOptions?.length) {
      return filterWidgetPorts(fromOptions, availableMotorPorts, isHistoric);
    }
    return defaultSelectedMotorPorts(dataKey, availableMotorPorts);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- options read for port values; selectedMotorPortsKey avoids parse-time array identity churn
  }, [availableMotorPorts, dataKey, isHistoric, isMotorWidget, selectedMotorPortsKey]);

  const selectedMechanismPorts = useMemo(() => {
    if (!isMechanismWidget) return [];
    const fromOptions = options?.selectedMotorPorts;
    if (fromOptions?.length) {
      return filterWidgetPorts(fromOptions, availableMechanismPorts, isHistoric);
    }
    return defaultSelectedMotorPorts(dataKey, availableMechanismPorts);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- options read for port values; selectedMechanismPortsKey avoids parse-time array identity churn
  }, [availableMechanismPorts, dataKey, isHistoric, isMechanismWidget, selectedMechanismPortsKey]);

  const selectedMotorMetrics = useMemo(() => {
    if (!isMotorMultiWidget) return [];
    return defaultSelectedMotorMetrics(dataKey, availableMotorMetrics, options?.selectedMotorMetrics);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- options read for metrics; selectedMotorMetricsKey avoids parse-time array identity churn
  }, [availableMotorMetrics, dataKey, isMotorMultiWidget, selectedMotorMetricsKey]);

  const selectedSensorPorts = useMemo(() => {
    if (!isSensorWidget) return [];
    const fromOptions = options?.selectedSensorPorts;
    if (fromOptions?.length) {
      return filterWidgetPorts(fromOptions, availableSensorPorts, isHistoric);
    }
    return defaultSelectedSensorPorts(dataKey, availableSensorPorts);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- options read for port values; selectedSensorPortsKey avoids parse-time array identity churn
  }, [availableSensorPorts, dataKey, isHistoric, isSensorWidget, selectedSensorPortsKey]);

  const selectedOtherPorts = useMemo(() => {
    if (!isOtherWidget) return [];
    const fromOptions = options?.selectedOtherPorts;
    if (fromOptions?.length) {
      return filterWidgetPorts(fromOptions, availableOtherPorts, isHistoric);
    }
    return defaultSelectedOtherPorts(dataKey, availableOtherPorts);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- options read for port values; selectedOtherPortsKey avoids parse-time array identity churn
  }, [availableOtherPorts, dataKey, isHistoric, isOtherWidget, selectedOtherPortsKey]);

  const widgetTitleOptionsKey = useMemo(
    () =>
      JSON.stringify({
        motorVariableName: options?.motorVariableName,
        selectedMotorPort: options?.selectedMotorPort,
        mechanismClassName: options?.mechanismClassName,
        sensorVariableName: options?.sensorVariableName,
        otherVariableName: options?.otherVariableName,
        allMotorPorts: options?.allMotorPorts,
        allSensorPorts: options?.allSensorPorts,
        allOtherPorts: options?.allOtherPorts,
        selectedMotorPortsKey,
        selectedMotorMetricsKey,
        selectedOtherPortsKey,
      }),
    [
      options?.motorVariableName,
      options?.selectedMotorPort,
      options?.mechanismClassName,
      options?.sensorVariableName,
      options?.otherVariableName,
      options?.allMotorPorts,
      options?.allSensorPorts,
      options?.allOtherPorts,
      selectedMotorPortsKey,
      selectedMotorMetricsKey,
      selectedOtherPortsKey,
    ]
  );

  const streamSensorKeys = useMemo(() => {
    if (isHistoric && layoutSensorKeys?.length) {
      return dedupeStreamKeysPreserveOrder(layoutSensorKeys);
    }

    const streamProjectContext = { portRows: driveUnitPorts, projectCode };
    if (isMotorMultiWidget) {
      return resolveWidgetSensorKeys(
        {
          name: MOTOR_MULTI_WIDGET_TYPE,
          dataKey,
          sensorKeys: layoutSensorKeys ?? [],
          curve,
          style,
          props: {},
          options: {
            ...options,
            selectedMotorPort: motorMultiPort ?? options?.selectedMotorPort,
            selectedMotorMetrics: selectedMotorMetrics.length > 0 ? selectedMotorMetrics : options?.selectedMotorMetrics,
          },
        },
        streamProjectContext
      );
    }
    if (isMotorWidget) {
      const metric = streamMetricSlug(dataKey);
      const keysForPorts = motorStreamKeysForSelectedPorts(driveUnitPorts, projectCode, metric, selectedMotorPorts, 'motor');
      if (keysForPorts.length) return keysForPorts;
      return motorStreamKeysForPorts(dataKey, selectedMotorPorts);
    }
    if (isMechanismWidget) {
      const metric = streamMetricSlug(dataKey);
      const keysForPorts = motorStreamKeysForSelectedPorts(driveUnitPorts, projectCode, metric, selectedMechanismPorts, 'mechanism');
      if (keysForPorts.length) return keysForPorts;
      return motorStreamKeysForPorts(dataKey, selectedMechanismPorts);
    }
    if (isSensorWidget) return sensorStreamKeysForPorts(dataKey, selectedSensorPorts);
    if (isOtherWidget) return otherStreamKeysForPorts(dataKey, selectedOtherPorts);
    return [drivebaseWidgetStreamDataKey(type, dataKey)];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- options read for motor_multi; port/metric keys avoid object identity churn
  }, [
    curve,
    dataKey,
    driveUnitPorts,
    isHistoric,
    isMechanismWidget,
    isMotorMultiWidget,
    isMotorWidget,
    isOtherWidget,
    isSensorWidget,
    layoutSensorKeys,
    motorMultiPort,
    projectCode,
    style,
    selectedMechanismPorts,
    selectedMotorMetrics,
    selectedMotorPorts,
    selectedOtherPorts,
    selectedSensorPorts,
    widgetTitleOptionsKey,
  ]);

  const liveGraphKeys = useMemo((): string[] => {
    const isCustomCallable = type === CUSTOM_CALLABLE_WIDGET_TYPE;
    if (electronicsPortStreamKind && streamSensorKeys.length > 1) {
      return [...streamSensorKeys];
    }
    const graphKey = electronicsPortStreamKind ? streamSensorKeys[0] ?? dataKey : isCustomCallable ? dataKey : type;
    return [graphKey];
  }, [dataKey, electronicsPortStreamKind, streamSensorKeys, type]);

  const sourceGraphs = useLiveGraphKeys(liveGraphKeys);

  const registeredCustomCallableParameters = useRegisteredCustomCallableParameters();

  const isCustomCallableStale =
    isCustomCallableWidget && !isHistoric && !isCustomCallableStreamRegisteredInCode(dataKey, registeredCustomCallableParameters);

  const motorMultiClassNameByPort = useMemo(() => {
    if (!isMotorMultiWidget) return undefined;
    return buildMotorClassNameByPort(driveUnitPorts, projectCode);
  }, [driveUnitPorts, isMotorMultiWidget, projectCode]);

  const motorMultiMechanismClassNameByPort = useMemo(() => {
    if (!isMotorMultiWidget) return undefined;
    const prefix = parseMotorMultiWidgetDataKey(dataKey)?.prefix ?? 'motor';
    if (prefix !== 'mechanism') return undefined;
    return buildMechanismClassNameByPort(driveUnitPorts, projectCode);
  }, [dataKey, driveUnitPorts, isMotorMultiWidget, projectCode]);

  const chartSeriesLabelOptions = useMemo((): ChartSeriesLabelOptions | undefined => {
    if (isCustomCallableWidget) {
      return {
        customCallableParameters: dataKey ? new Set([...registeredCustomCallableParameters, dataKey]) : registeredCustomCallableParameters,
      };
    }
    if (isMotorMultiWidget) {
      return {
        motorClassNameByPort: motorMultiClassNameByPort,
        mechanismClassNameByPort: motorMultiMechanismClassNameByPort,
        useMotorMetricSeriesLabels: true,
      };
    }
    if (isMotorWidget) {
      return {
        motorVariableNameByPort: resolveHistoricOrLiveLabelMap(isHistoric, historicPortDisplayLabels, () =>
          buildMotorChartSeriesLabelByPort(driveUnitPorts, projectCode)
        ),
        motorClassNameByPort: buildMotorClassNameByPort(driveUnitPorts, projectCode),
      };
    }
    if (isMechanismWidget) {
      return {
        motorVariableNameByPort: resolveHistoricOrLiveLabelMap(isHistoric, historicPortDisplayLabels, () =>
          buildMechanismVariableNameByPort(driveUnitPorts, projectCode)
        ),
        motorClassNameByPort: buildMotorClassNameByPort(driveUnitPorts, projectCode),
      };
    }
    if (isSensorWidget && sensorClassName) {
      return {
        sensorVariableNameByPort: resolveHistoricOrLiveLabelMap(isHistoric, historicPortDisplayLabels, () =>
          buildSensorVariableNameByPort(driveUnitPorts, projectCode, sensorClassName)
        ),
      };
    }
    if (isOtherWidget && otherClassName) {
      return {
        otherElectronicsVariableNameByPort: resolveHistoricOrLiveLabelMap(isHistoric, historicPortDisplayLabels, () =>
          buildOtherElectronicsChartSeriesLabelByPort(driveUnitPorts, projectCode, otherClassName)
        ),
      };
    }
    return undefined;
  }, [
    dataKey,
    registeredCustomCallableParameters,
    driveUnitPorts,
    historicPortDisplayLabels,
    isCustomCallableWidget,
    isHistoric,
    isMechanismWidget,
    isMotorMultiWidget,
    isMotorWidget,
    isOtherWidget,
    isSensorWidget,
    motorMultiClassNameByPort,
    motorMultiMechanismClassNameByPort,
    otherClassName,
    projectCode,
    sensorClassName,
  ]);

  const headerTitle = useMemo(() => {
    if (isCustomCallableWidget) {
      return resolveCustomCallableDisplayName(dataKey) || t('widgetBase.unknown-widget', 'Widget');
    }
    if (isMotorMultiWidget) {
      if (options?.motorVariableName) return options.motorVariableName;
      if (motorMultiPort !== null) return motorMultiWidgetDisplayTitle(motorMultiPort);
    }
    if (isMotorWidget || isMechanismWidget) {
      const motorMechanismTitle = resolveMotorMechanismWidgetTitle(type, dataKey, t, options, {
        selectedMotorPorts: isMechanismWidget ? selectedMechanismPorts : selectedMotorPorts,
        motorPortLabel: (port) => motorPortLabelByPort.get(port),
        mechanismPortLabel: (port) => mechanismPortLabelByPort.get(port),
      });
      if (motorMechanismTitle !== undefined) return motorMechanismTitle;
    }
    if (isSensorWidget) {
      const sensorTitle = resolveSensorWidgetTitle(type, dataKey, options, {
        selectedSensorPorts,
        sensorPortLabel: (port) => {
          const fromProject = sensorPortLabelByPort.get(port);
          if (fromProject) return fromProject;
          if (options?.sensorVariableName?.trim()) {
            return motorPortDisplayTitle(port, options.sensorVariableName);
          }
          return motorPortDisplayTitle(port, '');
        },
      });
      if (sensorTitle !== undefined) return sensorTitle;
    }
    if (isOtherWidget) {
      const otherTitle = resolveOtherWidgetTitle(type, dataKey, options, {
        selectedOtherPorts,
        otherPortLabel: (port) => {
          const fromProject = otherPortLabelByPort.get(port);
          if (fromProject) return fromProject;
          if (options?.otherVariableName?.trim()) {
            return motorPortDisplayTitle(port, options.otherVariableName);
          }
          return motorPortDisplayTitle(port, '');
        },
      });
      if (otherTitle !== undefined) return otherTitle;
    }
    return defaultWidgetTitle(type, dataKey, t, options) ?? type;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- options read for title; widgetTitleOptionsKey avoids object identity churn
  }, [
    dataKey,
    isCustomCallableWidget,
    isMechanismWidget,
    isMotorMultiWidget,
    isMotorWidget,
    isOtherWidget,
    isSensorWidget,
    mechanismPortLabelByPort,
    motorMultiPort,
    motorPortLabelByPort,
    otherPortLabelByPort,
    selectedMechanismPorts,
    selectedMotorPorts,
    selectedOtherPorts,
    selectedSensorPorts,
    sensorPortLabelByPort,
    widgetTitleOptionsKey,
    t,
    type,
  ]);

  const mergedElectronicsGraph = useMemo(() => {
    if (!electronicsPortStreamKind || streamSensorKeys.length <= 1) return undefined;
    return mergeElectronicsPortStreamGraphs(sourceGraphs, streamSensorKeys, electronicsPortStreamKind, graphCurve);
  }, [electronicsPortStreamKind, graphCurve, sourceGraphs, streamSensorKeys]);

  const graph = useMemo((): Graph | undefined => {
    if (mergedElectronicsGraph) return mergedElectronicsGraph;

    const isCustomCallable = type === CUSTOM_CALLABLE_WIDGET_TYPE;
    const graphKey = electronicsPortStreamKind ? streamSensorKeys[0] ?? dataKey : isCustomCallable ? dataKey : type;
    const storeGraph = find(propEq(graphKey, 'key'), sourceGraphs);
    if (!storeGraph) return undefined;

    const seriesDataKey = drivebaseWidgetStreamDataKey(type, dataKey);
    const series =
      electronicsPortStreamKind || dataKey === 'all'
        ? storeGraph.series.map((value) => ({ ...value, curve: graphCurve }))
        : storeGraph.series.filter((item) => item.dataKey === seriesDataKey).map((value) => ({ ...value, curve: graphCurve }));

    return {
      ...storeGraph,
      series,
      data: storeGraph.data,
    };
  }, [mergedElectronicsGraph, sourceGraphs, type, dataKey, graphCurve, electronicsPortStreamKind, streamSensorKeys]);

  /** Persist curve/style to project layout. Only call after explicit user changes — not on mount. */
  const persistWidgetSettings = useCallback(
    (patch: { curve?: CurveType; style?: string }) => {
      persistWidgetContent(patch);
    },
    [persistWidgetContent]
  );

  const handleMotorPortsChange = useMotorStreamPortsChange({
    dataKey,
    driveUnitPorts,
    projectCode,
    streamKind: 'motor',
    persistWidgetContent,
  });

  const handleMechanismPortsChange = useMotorStreamPortsChange({
    dataKey,
    driveUnitPorts,
    projectCode,
    streamKind: 'mechanism',
    persistWidgetContent,
  });

  const handleMotorMetricsChange = useCallback(
    (metrics: string[]) => {
      if (motorMultiPort === null) return;
      const clamped = clampMotorMultiMetrics(metrics, availableMotorMetrics);
      persistWidgetContent({
        sensorKeys: motorMultiStreamKeysForMetrics(dataKey, motorMultiPort, clamped, { portRows: driveUnitPorts, projectCode }),
        selectedMotorMetrics: clamped,
        selectedMotorPort: motorMultiPort,
      });
    },
    [availableMotorMetrics, dataKey, driveUnitPorts, motorMultiPort, persistWidgetContent, projectCode]
  );

  const handleSensorPortsChange = useCallback(
    (ports: number[]) => {
      persistWidgetContent({
        sensorKeys: sensorStreamKeysForPorts(dataKey, ports),
        selectedSensorPorts: ports,
        ...(sensorClassName ? { sensorClassName } : {}),
      });
    },
    [dataKey, persistWidgetContent, sensorClassName]
  );

  const handleOtherPortsChange = useCallback(
    (ports: number[]) => {
      persistWidgetContent({
        sensorKeys: otherStreamKeysForPorts(dataKey, ports),
        selectedOtherPorts: ports,
        ...(otherClassName ? { otherClassName } : {}),
      });
    },
    [dataKey, otherClassName, persistWidgetContent]
  );

  // Keep local render state in sync with persisted widget props when switching modes.
  // Otherwise, historic-mode-only edits can "stick" when returning to live mode even
  // though we never saved them to the project layout.
  useEffect(() => {
    setGraphCurve(curve);
    setWidgetStyle(style);
  }, [curve, isHistoric, style]);

  const handleLinear = useCallback(() => {
    setGraphCurve('linear');
    setWidgetStyle('line');
    persistWidgetSettings({ curve: 'linear', style: 'line' });
  }, [persistWidgetSettings]);

  const handleNatural = useCallback(() => {
    setGraphCurve('natural');
    setWidgetStyle('line');
    persistWidgetSettings({ curve: 'natural', style: 'line' });
  }, [persistWidgetSettings]);

  const handleStep = useCallback(() => {
    setGraphCurve('step');
    setWidgetStyle('line');
    persistWidgetSettings({ curve: 'step', style: 'line' });
  }, [persistWidgetSettings]);

  const handleBarGraph = useCallback(() => {
    setWidgetStyle('bar');
    persistWidgetSettings({ style: 'bar' });
  }, [persistWidgetSettings]);

  const handleValueTable = useCallback(() => {
    setWidgetStyle('raw');
    persistWidgetSettings({ style: 'raw' });
  }, [persistWidgetSettings]);

  const handleLiveReading = useCallback(() => {
    setWidgetStyle('number');
    persistWidgetSettings({ style: 'number' });
  }, [persistWidgetSettings]);

  const chooseMotorsToolbar = useChooseMotorsToolbar({
    show: isMotorWidget && shouldShowAllMotorPortPicker(options, motorPickerEntries),
    isHistoricMode: isHistoric,
    motors: motorPickerEntries,
    selectedPorts: selectedMotorPorts,
    onSelectedPortsChange: handleMotorPortsChange,
    disabled: !canEditWidgetDataSelection,
    selectedPortsKey: selectedMotorPortsKey,
  });

  const chooseMechanismsToolbar = useChooseMechanismsToolbar({
    show: isMechanismWidget && shouldShowAllMotorPortPicker(options, mechanismPickerEntries),
    isHistoricMode: isHistoric,
    mechanisms: mechanismPickerEntries,
    selectedPorts: selectedMechanismPorts,
    onSelectedPortsChange: handleMechanismPortsChange,
    disabled: !canEditWidgetDataSelection,
    selectedPortsKey: selectedMechanismPortsKey,
  });

  const chooseSensorsToolbar = useChooseSensorsToolbar({
    show: isSensorWidget && shouldShowAllSensorPortPicker(options, sensorPickerEntries),
    isHistoricMode: isHistoric,
    sensors: sensorPickerEntries,
    selectedPorts: selectedSensorPorts,
    onSelectedPortsChange: handleSensorPortsChange,
    disabled: !canEditWidgetDataSelection,
    selectedPortsKey: selectedSensorPortsKey,
  });

  const chooseOtherElectronicsToolbar = useChooseOtherElectronicsToolbar({
    show: isOtherWidget && shouldShowAllOtherPortPicker(options, otherPickerEntries),
    isHistoricMode: isHistoric,
    adapters: otherPickerEntries,
    selectedPorts: selectedOtherPorts,
    onSelectedPortsChange: handleOtherPortsChange,
    disabled: !canEditWidgetDataSelection,
    selectedPortsKey: selectedOtherPortsKey,
  });

  const choosePortsToolbar = useMemo(() => {
    if (isHistoric) return null;
    if (isMotorMultiWidget && motorDataPickerEntries.length > 1) {
      return (
        <ChooseMotorDataToolbarButton
          entries={motorDataPickerEntries}
          selectedMetrics={selectedMotorMetrics}
          onSelectedMetricsChange={handleMotorMetricsChange}
          disabled={!canEditWidgetDataSelection}
        />
      );
    }
    return chooseMotorsToolbar ?? chooseMechanismsToolbar ?? chooseSensorsToolbar ?? chooseOtherElectronicsToolbar;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- options read for allMotorPorts/allSensorPorts/allOtherPorts; widgetTitleOptionsKey avoids object identity churn
  }, [
    chooseMechanismsToolbar,
    chooseMotorsToolbar,
    chooseOtherElectronicsToolbar,
    chooseSensorsToolbar,
    handleMotorMetricsChange,
    isHistoric,
    isMotorMultiWidget,
    motorDataPickerEntries,
    canEditWidgetDataSelection,
    selectedMotorMetrics,
    widgetTitleOptionsKey,
  ]);

  const graphMenuItems = useMemo(() => {
    if (isOtherWidget) return undefined;
    return (
      <>
        <MenuItem onClick={handleLinear}>
          <Trans i18nKey="lineGraphWidget.menuItem.linear">Line Graph (linear)</Trans>
        </MenuItem>
        <MenuItem onClick={handleNatural}>
          <Trans i18nKey="lineGraphWidget.menuItem.natural">Line Graph (natural)</Trans>
        </MenuItem>
        <MenuItem onClick={handleStep}>
          <Trans i18nKey="lineGraphWidget.menuItem.step">Line Graph (step)</Trans>
        </MenuItem>
        {type === 'dist' && (
          <MenuItem onClick={handleBarGraph}>
            <Trans i18nKey="lineGraphWidget.menuItem.bar-graph">Bar Graph</Trans>
          </MenuItem>
        )}
        <MenuItem onClick={handleValueTable}>
          <Trans i18nKey="lineGraphWidget.menuItem.value-table">Value Table Reading</Trans>
        </MenuItem>
        <MenuItem onClick={handleLiveReading}>
          <Trans i18nKey="lineGraphWidget.menuItem.live-reading">Live Number Reading</Trans>
        </MenuItem>
      </>
    );
  }, [handleBarGraph, handleLinear, handleLiveReading, handleNatural, handleStep, handleValueTable, isOtherWidget, type]);

  useWidgetHeader(
    {
      title: headerTitle,
      menuItems: graphMenuItems,
      toolbarEnd: choosePortsToolbar,
    },
    [headerTitle, graphMenuItems, choosePortsToolbar]
  );

  return (
    <Box display="flex" flexDirection="column" overflow="hidden" width="100%" height="100%" flex={1} minHeight={0}>
      {isCustomCallableStale ? (
        <GraphNoDataMessage i18nKey="graphWidget.customCallable.notRegisteredInCode" defaultMessage="Stream not registered in code" />
      ) : (
        <>
          {effectiveStyle === 'line' && <LineGraphWidget graph={graph} chartSeriesLabelOptions={chartSeriesLabelOptions} />}
          {effectiveStyle === 'number' && <LiveNumberWidget graph={graph} chartSeriesLabelOptions={chartSeriesLabelOptions} />}
          {effectiveStyle === 'raw' && <RawDataWidget graph={graph} />}
          {effectiveStyle === 'bar' && <BarGraphWidget graph={graph} chartSeriesLabelOptions={chartSeriesLabelOptions} />}
        </>
      )}
    </Box>
  );
};

export default GraphWidget;
